// ---------------------------------------------------------------------------
// The letter tile: the signature element, and the thing every letters screen is
// built around.
//
// Nine `phone` tiles have to fit a 390px portrait viewport, which is where
// 36 x 46 with a 5px gap comes from (354px, and a 46px tap target). The tile
// body keeps its dark blue in light mode on purpose: a pale tile stops reading
// as a tile.
//
// A tile with an `onClick` is a real button; a tile without one is a div, so a
// board that cannot be tapped does not advertise itself to a screen reader as
// nine controls.
//
// **`fit` is how a row of tiles stops being wider than the room it is in.** The
// sizes above are a fixed pixel table, and nine of anything from `board` up is
// wider than the reveal's stage at every desktop width — 788px of `board` tiles
// into 762 — so a nine-letter word wrapped onto a second row, which is not what
// a nine-letter word is. A fitted tile takes `min(its designed width, its share
// of the row)` and derives everything else from that, so the row shrinks to fit
// and never wraps. See `tileFitStyle`.
// ---------------------------------------------------------------------------

import type { CSSProperties, ReactElement } from 'react';
import { Padlock } from './glyphs.js';

export type LetterTileState =
  | 'face-down'
  | 'revealing'
  | 'available'
  | 'used'
  | 'locked'
  | 'highlight';

export type LetterTileSize = 'phone' | 'wordmark' | 'board' | 'selection' | 'desktop' | 'inline';

export interface LetterTileProps {
  letter?: string;
  state: LetterTileState;
  size?: LetterTileSize;
  /**
   * Take `--tile-fit` as the width instead of the size table, and scale the
   * height, the radius and the letter with it. Set by `tileFitStyle` on the row.
   */
  fit?: boolean;
  onClick?: () => void;
  className?: string;
}

interface TileGeometry {
  width: number;
  height: number;
  radius: number;
  fontSize: number;
}

const GEOMETRY: Record<LetterTileSize, TileGeometry> = {
  phone: { width: 36, height: 46, radius: 5, fontSize: 24 },
  // The home page wordmark. Nine of these plus their gaps come to 616px, which
  // fits the hero column of the 1180px desktop grid; nine `board` tiles do not,
  // and pushed the whole page into horizontal scroll.
  wordmark: { width: 64, height: 80, radius: 7, fontSize: 40 },
  board: { width: 84, height: 104, radius: 8, fontSize: 52 },
  selection: { width: 96, height: 118, radius: 9, fontSize: 58 },
  desktop: { width: 104, height: 126, radius: 10, fontSize: 64 },
  inline: { width: 28, height: 34, radius: 4, fontSize: 17 },
};

/**
 * The style that makes `count` tiles fit the row they are in, whatever it is.
 *
 * Goes on the row; the tiles read it through `fit`. The row's own ancestor has
 * to carry `.tile-fit`, which is the query container `100cqw` is measured
 * against — see index.css.
 *
 * Container queries rather than a ResizeObserver, and rather than a transform.
 * A ResizeObserver measures after a paint, so the first frame of every reveal
 * would be the wrapped layout it exists to prevent, and every resize would be a
 * measure-render-measure loop in JavaScript. A transform would scale the row
 * without telling the layout, so a shrunken row would keep the footprint of the
 * big one and the frame around it would size to a word that is not there. This
 * resolves at layout time, costs nothing at runtime, and the fallback in the
 * `var()` is the designed size, so a browser without container queries gets
 * exactly what shipped before.
 */
export function tileFitStyle(
  size: LetterTileSize,
  count: number,
  gap = 4,
): CSSProperties {
  const { width } = GEOMETRY[size];
  if (count <= 0) return {};
  const share = `calc((100cqw - ${(count - 1) * gap}px) / ${count})`;
  return { ['--tile-fit' as string]: `min(${width}px, ${share})` } as CSSProperties;
}

const TILE_FACE = 'linear-gradient(var(--c-tile-top), var(--c-tile-bottom))';

// The back of a tile, painted once and used twice: by a `face-down` slot, and as
// the reverse of a `revealing` one. They are the same two declarations on
// purpose — a flip that starts on anything other than the slot it replaced snaps
// on its first frame. See `tile-flip-in` in index.css.
const TILE_BACK = 'linear-gradient(var(--c-tile-back-top), var(--c-tile-back-bottom))';

const TILE_BACK_EDGE = 'inset 0 1px 0 var(--c-tile-back-edge)';

const RELIEF = 'inset 0 2px 0 var(--c-tile-relief-top), inset 0 -3px 0 var(--c-tile-relief-bottom)';

export function LetterTile({
  letter,
  state,
  size = 'phone',
  fit = false,
  onClick,
  className,
}: LetterTileProps): ReactElement {
  const { width, height, radius, fontSize } = GEOMETRY[size];
  const scale = width / 104;
  const showLetter = state !== 'face-down' && Boolean(letter);

  // Every dimension is a ratio of the width, so one variable moves all of them
  // together and a fitted tile is the designed tile at a smaller scale rather
  // than a squashed one. Unfitted, these are the same numbers as before.
  const span = (value: number): string | number =>
    fit ? `calc(var(--tile-fit, ${width}px) * ${Number((value / width).toFixed(4))})` : value;

  const face: CSSProperties = { borderRadius: span(radius) };
  let letterColor = 'var(--c-tile-letter)';
  let letterShadow: string | undefined;
  let letterOpacity: number | undefined;
  let faceClass = '';

  switch (state) {
    case 'face-down':
      face.background = TILE_BACK;
      face.boxShadow = TILE_BACK_EDGE;
      break;

    // A tile that is arriving is a tile, plus the animation of it arriving. It
    // is deliberately identical to `available` in every painted value: the flip
    // starts from the face-down slot and ends on the resting face, so there is
    // nothing to switch on at either end of it.
    //
    // It used to hold a bright blue face and a 40px glow for as long as it was
    // the most recent tile, which made the last tile called a highlighted tile
    // rather than a tile that had just landed, and put a snap at the end of
    // every flip when the next call took the highlight away.
    //
    // `tile-flip-front` is the only thing this state adds to the face, and it
    // adds no paint: it is the half of the card the letter is printed on, and
    // all it says is that its own reverse is not to be drawn. The turn itself
    // belongs to the rotator this face is wrapped in, below.
    case 'revealing':
      face.background = TILE_FACE;
      faceClass = 'tile-relief tile-flip-front';
      letterShadow = '0 2px 0 var(--c-tile-letter-shadow)';
      break;

    case 'available':
      face.background = TILE_FACE;
      faceClass = 'tile-relief';
      letterShadow = '0 2px 0 var(--c-tile-letter-shadow)';
      break;

    case 'highlight':
      face.background = TILE_FACE;
      // Composed here rather than via `tile-relief` because the accent ring has
      // to join the same box-shadow list.
      face.boxShadow = `${RELIEF}, 0 0 0 3px var(--c-accent)`;
      letterShadow = '0 2px 0 var(--c-tile-letter-shadow)';
      break;

    case 'used':
      face.background = 'var(--c-tile-used-body)';
      face.border = '2px dashed var(--c-tile-used-edge)';
      letterColor = 'var(--c-tile-used-glyph)';
      break;

    case 'locked':
      face.background = 'linear-gradient(var(--c-tile-locked-top), var(--c-tile-locked-bottom))';
      face.filter = 'saturate(0.15)';
      letterColor = 'var(--c-tile-locked-glyph)';
      letterOpacity = 0.8;
      break;
  }

  // The blank circle on the back of a tile. One element, rendered by the
  // `face-down` slot and by the reverse of a flipping tile, so the two cannot
  // drift apart and leave the flip starting on a back nobody had been looking at.
  const backMark = (
    <div
      className="rounded-full"
      style={{
        width: width * 0.33,
        height: width * 0.33,
        border: `${Math.max(1.5, 2 * scale)}px solid var(--c-tile-back-edge)`,
      }}
    />
  );

  const front = (
    <div
      className={`relative grid h-full w-full place-items-center ${faceClass}`}
      // Only the relief and the saturation are transitioned. The face itself
      // goes from a gradient to a flat colour, and those two do not interpolate:
      // a browser asked to transition them drops the gradient on frame one and
      // fades the colour up from transparent, which flashes the page through the
      // tile. Spending a tile has to be instant anyway.
      //
      // A tile arriving transitions NOTHING. The flip turns this face over for
      // exactly TILE_FLIP_MS, so a 180ms ramp underneath it would be a second
      // arrival on a second clock. The flip is the arrival.
      style={{
        ...face,
        transition:
          state === 'revealing' ? 'none' : 'box-shadow 180ms ease-out, filter 180ms ease-out',
      }}
      data-tile-state={state}
    >
      {showLetter && (
        // Nothing here fades in on a reveal. The letter is printed on the front
        // of the card and the card is showing the viewer its back until it is
        // past halfway, so it is hidden by geometry rather than by opacity.
        <span
          className="font-sans leading-none font-extrabold transition-[color,opacity] duration-180 ease-out"
          style={{
            fontSize: span(fontSize),
            color: letterColor,
            textShadow: letterShadow,
            opacity: letterOpacity,
            letterSpacing: '-0.02em',
          }}
        >
          {letter}
        </span>
      )}

      {state === 'face-down' && backMark}

      {state === 'locked' && (
        <div
          className="absolute"
          style={{
            bottom: Math.max(3, 9 * scale),
            right: Math.max(3, 11 * scale),
            color: 'var(--c-tile-locked-glyph)',
          }}
        >
          <Padlock size={Math.max(9, 19 * scale)} />
        </div>
      )}
    </div>
  );

  // A tile that is arriving is a card being turned over, so it is two faces in
  // one rotator rather than one face being animated. The rotator holds the 3D
  // context and the turn; the faces are stacked in it, each hiding its own
  // reverse. Everything in here sizes off the tile — `h-full w-full` and
  // `inset-0` — so a fitted tile shrinks whole and the row still cannot wrap.
  //
  // Only `revealing` pays for the extra element. Every other state is the single
  // face it always was, which also keeps the board's `firstElementChild` the
  // face for the play screen's own entrance animation.
  const body =
    state === 'revealing' ? (
      <div className="tile-flip-in relative h-full w-full">
        <div
          className="tile-flip-back absolute inset-0 grid place-items-center"
          style={{ borderRadius: span(radius), background: TILE_BACK, boxShadow: TILE_BACK_EDGE }}
        >
          {backMark}
        </div>
        {front}
      </div>
    ) : (
      front
    );

  const outer = `shrink-0 ${className ?? ''}`;
  const style: CSSProperties = {
    width: fit ? `var(--tile-fit, ${width}px)` : width,
    height: span(height),
    perspective: 600,
  };

  if (!onClick) {
    return (
      <div className={outer} style={style}>
        {body}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={showLetter ? undefined : 'face-down tile'}
      className={`${outer} active:translate-y-[1px]`}
      style={style}
    >
      {body}
    </button>
  );
}
